import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useShop } from './ShopContext';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../utils/apiError';

const API_URL = process.env.REACT_APP_API_URL;

const CartContext = createContext(null);

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(false);
    const { token, getAuthHeaders } = useAuth();
    const { merchantId } = useShop();

    // Dynamic cart storage key based on merchant
    const CART_STORAGE_KEY = `instashop_cart_${merchantId || 'default'}`;

    // Load cart from localStorage for guests
    const loadLocalCart = useCallback(() => {
        try {
            const stored = localStorage.getItem(CART_STORAGE_KEY);
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (e) {
            console.error('Failed to load cart from localStorage:', e);
        }
        return { items: [], total: 0 };
    }, [CART_STORAGE_KEY]);

    // Save cart to localStorage for guests
    const saveLocalCart = useCallback((cartData) => {
        try {
            localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
        } catch (e) {
            console.error('Failed to save cart to localStorage:', e);
        }
    }, [CART_STORAGE_KEY]);

    // Fetch cart from server (for logged-in users)
    const fetchServerCart = useCallback(async () => {
        if (!token) return null;
        try {
            const response = await axios.get(`${API_URL}/cart`, {
                headers: getAuthHeaders()
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch cart from server:', error);
            return null;
        }
    }, [token, getAuthHeaders]);

    // Merge local cart with server cart when user logs in
    const mergeCartsOnLogin = useCallback(async () => {
        if (!token) return;

        const localCart = loadLocalCart();
        if (localCart.items.length === 0) {
            // No local items to merge, just fetch server cart
            const serverCart = await fetchServerCart();
            if (serverCart) setCart(serverCart);
            return;
        }

        // Add each local item to server cart
        try {
            setLoading(true);
            for (const item of localCart.items) {
                await axios.post(`${API_URL}/cart/add`, 
                    { product_id: item.product_id, quantity: item.quantity },
                    { headers: getAuthHeaders() }
                );
            }
            // Clear local cart after merge
            localStorage.removeItem(CART_STORAGE_KEY);
            // Fetch merged cart from server
            const serverCart = await fetchServerCart();
            if (serverCart) setCart(serverCart);
            toast.success('Το καλάθι σας συγχωνεύτηκε!');
        } catch (error) {
            console.error('Failed to merge carts:', error);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, loadLocalCart, fetchServerCart, getAuthHeaders, CART_STORAGE_KEY]);

    // Initialize cart
    useEffect(() => {
        if (token) {
            mergeCartsOnLogin();
        } else {
            setCart(loadLocalCart());
        }
    }, [token, loadLocalCart, mergeCartsOnLogin]);

    // Add to cart
    const addToCart = async (product, quantity = 1) => {
        const stock = product.stock ?? 999999;
        const qty = Math.min(Math.max(1, quantity), stock);
        if (token) {
            // Logged in - use server
            try {
                await axios.post(`${API_URL}/cart/add`, 
                    { product_id: product.id, quantity: qty },
                    { headers: getAuthHeaders() }
                );
                const serverCart = await fetchServerCart();
                if (serverCart) setCart(serverCart);
                toast.success('Προστέθηκε στο καλάθι');
                return true;
            } catch (error) {
                toast.error(
                    formatApiErrorDetail(error.response?.data?.detail, 'Αποτυχία προσθήκης στο καλάθι')
                );
                return false;
            }
        } else {
            // Guest - use localStorage (quantity capped by stock)
            const localCart = loadLocalCart();
            const existingIndex = localCart.items.findIndex(i => i.product_id === product.id);
            
            if (existingIndex >= 0) {
                localCart.items[existingIndex].quantity = Math.min(localCart.items[existingIndex].quantity + qty, stock);
                localCart.items[existingIndex].subtotal = localCart.items[existingIndex].price * localCart.items[existingIndex].quantity;
            } else {
                localCart.items.push({
                    product_id: product.id,
                    name: product.name,
                    price: product.price,
                    quantity: qty,
                    image_url: product.image_url,
                    subtotal: product.price * qty,
                    shop_owner_id: merchantId
                });
            }
            
            localCart.total = localCart.items.reduce((sum, item) => sum + item.subtotal, 0);
            saveLocalCart(localCart);
            setCart(localCart);
            toast.success('Προστέθηκε στο καλάθι');
            return true;
        }
    };

    // Update cart item quantity
    const updateCartItem = async (productId, quantity) => {
        if (token) {
            try {
                await axios.put(`${API_URL}/cart/update`,
                    { product_id: productId, quantity },
                    { headers: getAuthHeaders() }
                );
                const serverCart = await fetchServerCart();
                if (serverCart) setCart(serverCart);
            } catch (error) {
                toast.error('Αποτυχία ενημέρωσης καλαθιού');
            }
        } else {
            const localCart = loadLocalCart();
            const item = localCart.items.find(i => i.product_id === productId);
            if (item) {
                item.quantity = quantity;
                item.subtotal = item.price * quantity;
                localCart.total = localCart.items.reduce((sum, i) => sum + i.subtotal, 0);
                saveLocalCart(localCart);
                setCart(localCart);
            }
        }
    };

    // Remove from cart
    const removeFromCart = async (productId) => {
        if (token) {
            try {
                await axios.delete(`${API_URL}/cart/remove/${productId}`, {
                    headers: getAuthHeaders()
                });
                const serverCart = await fetchServerCart();
                if (serverCart) setCart(serverCart);
                toast.success('Αφαιρέθηκε από το καλάθι');
            } catch (error) {
                toast.error('Αποτυχία αφαίρεσης');
            }
        } else {
            const localCart = loadLocalCart();
            localCart.items = localCart.items.filter(i => i.product_id !== productId);
            localCart.total = localCart.items.reduce((sum, i) => sum + i.subtotal, 0);
            saveLocalCart(localCart);
            setCart(localCart);
            toast.success('Αφαιρέθηκε από το καλάθι');
        }
    };

    // Clear cart
    const clearCart = async () => {
        if (token) {
            try {
                await axios.delete(`${API_URL}/cart/clear`, {
                    headers: getAuthHeaders()
                });
            } catch (error) {
                console.error('Failed to clear server cart:', error);
            }
        }
        localStorage.removeItem(CART_STORAGE_KEY);
        setCart({ items: [], total: 0 });
    };

    const itemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

    const value = {
        cart,
        loading,
        addToCart,
        updateCartItem,
        removeFromCart,
        clearCart,
        itemCount
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};
